import { Card, DataList, EmptyView, LoadingView, StatusBadge, DashStyles as Styles, TableWrap } from '../../components/dashboard/DashboardUI';
import { FeedbackMessage } from '../../components/dashboard/FeedbackMessage';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { BuildPlanFeatureBullets } from '../../components/landing/PricingCard';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { Button, Label } from '@app/engine';
import { CommonConstants, FormatLocalized, FormatTimestamp, Logger, ResolveCurrencyAmount, ResolveSupportedCurrency } from '@shared/common';
import { BillingClient, Telemetry, TenantClient } from '@shared/frontend';
import { useState } from 'react';
import type { IFeedbackDTO } from '../../components/dashboard/FeedbackMessage';

/** File-local constants. */
const localConstants = {
  logContext: 'Billing',
  minorUnitsPerUnit: 100,
  // Sentinel select value for "no plan" (schedules a period-end cancel when applied). Not a UUID, so it can never collide with a real plan id.
  nonePlanValue: '__none__'
} as const;

/**
 * Formats a minor-unit amount as a currency string in the user's selected app language.
 *
 * @param amountMinor - Amount in minor units (e.g. cents).
 * @param currency - ISO currency code.
 * @param locale - The user's selected app locale (BCP-47), so separators and symbol placement follow the in-app language rather than the browser locale.
 * @returns A formatted currency string.
 */
function FormatMoney(amountMinor: number, currency: string, locale: string): string {
  const value = amountMinor / localConstants.minorUnitsPerUnit;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/**
 * Maps a usage meter key to its friendly dashboard label. The `messages` meter presents as "Messages" because it covers every message channel (owner SMS/email notifications and customer texting,
 * both directions). Legacy `sms_notifications` rows (pre-rename usage events) render with the same label. Falls back to the raw key so a future meter never renders blank.
 *
 * @param meterKey - The usage meter key from the summary row.
 * @returns The display label for the meter.
 */
function FormatMeterLabel(meterKey: string): string {
  const labels: Record<string, string> = {
    [CommonConstants.Database.MeterKey.AnsweredCalls]: Localized.BillingMeterAnsweredCalls,
    [CommonConstants.Database.MeterKey.VoiceMinutes]: Localized.BillingMeterVoiceMinutes,
    [CommonConstants.Database.MeterKey.Messages]: Localized.BillingMeterNotifications,
    // Legacy meter key carried by usage rows recorded before the rename to `messages`.
    sms_notifications: Localized.BillingMeterNotifications
  };
  return labels[meterKey] ?? meterKey;
}

/**
 * Billing screen: subscription status, period usage, invoices, and the Stripe portal entry point.
 *
 * @returns The billing element.
 */
export function Billing() {
  const { tenantID } = useDashboard();
  const { selectedLang } = useLocale();
  const subscription = useDashboardData((signal) => BillingClient.GetSubscription(signal, tenantID!), [tenantID]);
  const summary = useDashboardData((signal) => BillingClient.GetSummary(signal, tenantID!), [tenantID]);
  const access = useDashboardData((signal) => BillingClient.GetAccess(signal, tenantID!), [tenantID]);
  // Full details of the plan the tenant is actually on (resolved by id, so it stays visible even after the plan is retired from the public catalog). Drives the read-only current-plan panel.
  const currentPlan = useDashboardData((signal) => BillingClient.GetCurrentPlan(signal, tenantID!, selectedLang), [tenantID, selectedLang]);
  // Self-selectable plan catalog (active public plans, translated). Drives the change-plan selector.
  const plans = useDashboardData((signal) => BillingClient.ListPublicPlans(signal, selectedLang), [selectedLang]);
  // The tenant row carries the effective concurrent-line cap (Portal-overridable; copied from the plan on subscribe/switch), so the current-plan panel shows the tenant's actual limit.
  const tenant = useDashboardData((signal) => TenantClient.GetMyTenant(signal), []);
  const [portalBusy, setPortalBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);
  // Pending selector value (null until the user changes it), the in-flight flag, and whether the inline confirm step is showing.
  const [planChoice, setPlanChoice] = useState<string | null>(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [planConfirming, setPlanConfirming] = useState(false);

  const openPortal = async () => {
    setPortalBusy(true);
    setFeedback(null);
    try {
      const controller = new AbortController();
      const response = await BillingClient.CreatePortalSession(controller.signal, tenantID!);
      Telemetry.BillingPortalOpened(subscription.data?.status);
      window.location.href = response.url;
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: Localized.DashError, type: 'error' });
      setPortalBusy(false);
    }
  };

  // Recovery path: pull the live subscription + latest invoice from the provider, then reload the affected panels. Lets a tenant who has just paid or updated their card unblock their AI without
  // waiting for the provider to redeliver the webhook.
  const refreshStatus = async () => {
    setRefreshBusy(true);
    setFeedback(null);
    try {
      const controller = new AbortController();
      await BillingClient.RefreshAccess(controller.signal, tenantID!);
      subscription.reload();
      currentPlan.reload();
      access.reload();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: Localized.DashError, type: 'error' });
    } finally {
      setRefreshBusy(false);
    }
  };

  // During the past-due grace window AccessGate still reports allowAI=true, so the grace state must be checked before the active state — otherwise a failed payment would render as "active".
  const accessBanner = !access.data
    ? null
    : access.data.inGracePeriod
      ? { className: Styles.warnText, text: Localized.BillingAccessGrace, graceEndsAt: access.data.graceEndsAt, canRefresh: true }
      : access.data.allowAI
        ? {
            className: Styles.successTextStart,
            // When an active subscription carries a coupon, fold the discount blurb into the "subscription is active" line (active-only per product spec).
            text: subscription.data?.couponPercentOff ? `${Localized.BillingAccessOk} ${FormatLocalized(Localized.BillingCouponBlurb, subscription.data.couponPercentOff)}` : Localized.BillingAccessOk,
            graceEndsAt: undefined,
            canRefresh: false
          }
        : { className: Styles.errorTextStart, text: Localized.BillingAccessBlocked, graceEndsAt: undefined, canRefresh: true };

  // Plan-change selector state. A fully canceled (or absent) subscription cannot be updated on the provider — re-subscribing goes through hosted checkout; a pending period-end cancel renders as
  // "None" so re-picking a plan reads as resuming.
  const sub = subscription.data ?? null;
  const subCanceled = sub?.status === CommonConstants.Database.BillingStatus.Canceled;
  const needsCheckout = !sub || subCanceled;
  const currentSelectValue = !sub || subCanceled || sub.cancelAtPeriodEnd ? localConstants.nonePlanValue : sub.planID;
  const selectedValue = planChoice ?? currentSelectValue;
  const planList = plans.data ?? [];
  const selectedPlan = planList.find((plan) => plan.id === selectedValue) ?? null;
  // The current plan may be private (not in the public catalog); keep it selectable so the dropdown can always render the truthful current value.
  const currentPlanMissing = sub && !subCanceled && !planList.some((plan) => plan.id === sub.planID);
  const applyDisabled = planBusy || plans.loading || selectedValue === currentSelectValue || (selectedValue === localConstants.nonePlanValue && needsCheckout);

  // The confirm copy depends on what Apply will actually do: schedule a cancel, resume a pending cancel, redirect to checkout, or switch plans.
  const confirmText =
    selectedValue === localConstants.nonePlanValue
      ? FormatLocalized(Localized.BillingConfirmCancelPlan, sub ? FormatTimestamp(sub.currentPeriodEnd) : '')
      : needsCheckout
        ? FormatLocalized(Localized.BillingConfirmResubscribe, selectedPlan?.name ?? '')
        : sub?.cancelAtPeriodEnd && selectedValue === sub.planID
          ? FormatLocalized(Localized.BillingConfirmResume, selectedPlan?.name ?? sub.planName ?? '')
          : FormatLocalized(Localized.BillingConfirmSwitch, selectedPlan?.name ?? '');

  // Applies the confirmed selection: period-end cancel for "None", hosted checkout for a canceled/missing subscription, otherwise an immediate prorated provider-first switch (which also resumes
  // a pending cancel — including re-picking the same plan).
  const applyPlanChange = async () => {
    setPlanBusy(true);
    setFeedback(null);
    try {
      const controller = new AbortController();
      if (selectedValue === localConstants.nonePlanValue) {
        const updated = await BillingClient.CancelTenantSubscription(controller.signal, tenantID!);
        setFeedback({ text: FormatLocalized(Localized.BillingCancelScheduled, FormatTimestamp(updated.currentPeriodEnd)), type: 'success' });
      } else if (needsCheckout) {
        const plan = planList.find((entry) => entry.id === selectedValue);
        if (plan) {
          const { url } = await BillingClient.CreateCheckoutSession(controller.signal, { tenantID: tenantID!, planCode: plan.code });
          // Full-page handoff to provider-hosted checkout; busy state intentionally persists until navigation.
          window.location.href = url;
          return;
        }
      } else {
        const resuming = sub?.cancelAtPeriodEnd && selectedValue === sub.planID;
        await BillingClient.ChangeTenantPlan(controller.signal, tenantID!, selectedValue);
        setFeedback({ text: resuming ? Localized.BillingResumed : Localized.BillingPlanChanged, type: 'success' });
      }
      setPlanChoice(null);
      subscription.reload();
      currentPlan.reload();
      access.reload();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      // Surface the server's reason (e.g. "complete checkout first") — the client throws with the response message when present.
      setFeedback({ text: Logger.GetErrorMessage(err) || Localized.DashError, type: 'error' });
    } finally {
      setPlanBusy(false);
      setPlanConfirming(false);
    }
  };

  // Current-plan pricing shows in the subscription's settlement currency (locked at checkout), falling back to the plan's default currency when the subscription is absent.
  const planSettlementCurrency = subscription.data?.currency ?? currentPlan.data?.currency ?? null;
  const currentPlanPricing = (() => {
    const plan = currentPlan.data;
    if (!plan || !planSettlementCurrency) return null;
    const effectiveCurrency = ResolveSupportedCurrency(plan.baseAmountByCurrency, plan.currency, planSettlementCurrency);
    const baseAmountMinor = ResolveCurrencyAmount(plan.baseAmount, plan.baseAmountByCurrency, plan.currency, effectiveCurrency);
    return {
      name: plan.name,
      features: [...plan.features, ...BuildPlanFeatureBullets(plan, effectiveCurrency, tenant.data?.concurrentLimit)],
      // A zero base fee renders as FREE (no currency string, no /mo suffix) rather than "$0.00".
      free: baseAmountMinor === 0,
      monthly: FormatMoney(baseAmountMinor, effectiveCurrency, selectedLang),
      setupFee: plan.setupAmount > 0 ? FormatMoney(ResolveCurrencyAmount(plan.setupAmount, plan.setupAmountByCurrency, plan.currency, effectiveCurrency), effectiveCurrency, selectedLang) : null
    };
  })();

  return (
    <div className={`${Styles.cardRow} ${Styles.cardRowTop}`}>
      <div className={Styles.accountStack}>
        {!subscription.loading && !plans.loading && planList.length > 0 ? (
          <Card title={Localized.BillingChangePlan} feedback={<FeedbackMessage feedback={feedback} />}>
            {/* Messaging bubble (no field name): the plan-change confirm prompt while in process, otherwise the subscription status line. All copy left-aligned. */}
            <div className={`${Styles.msgBubble} ${Styles.msgStart}`}>
              {planConfirming ? (
                <>
                  <Label text={confirmText} />
                  <div className={Styles.headerActions}>
                    <Button
                      text={planBusy ? Localized.DashLoading : Localized.BillingConfirmAction}
                      disabled={planBusy}
                      classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
                      classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
                      onClickHandler={() => void applyPlanChange()}
                    />
                    <Button
                      text={Localized.SharedCancel}
                      disabled={planBusy}
                      classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
                      classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
                      onClickHandler={() => setPlanConfirming(false)}
                    />
                  </div>
                </>
              ) : accessBanner ? (
                <Label
                  classNameContainer={accessBanner.className}
                  text={`${accessBanner.text}${accessBanner.graceEndsAt ? ` ${Localized.BillingAccessGraceUntil} ${FormatTimestamp(accessBanner.graceEndsAt)}.` : ''}`}
                />
              ) : null}
            </div>

            <div className={Styles.subPanel}>
              <div className={Styles.headerActions}>
                <select
                  className={Styles.select}
                  value={selectedValue}
                  disabled={planBusy}
                  onChange={(event) => {
                    setPlanChoice(event.target.value);
                    setPlanConfirming(false);
                  }}
                >
                  {currentPlanMissing && sub ? <option value={sub.planID}>{sub.planName ?? sub.planID}</option> : null}
                  {planList.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                  <option value={localConstants.nonePlanValue}>{Localized.BillingPlanNone}</option>
                </select>
                <Button
                  text={Localized.BillingApply}
                  disabled={applyDisabled || planConfirming}
                  classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
                  classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
                  onClickHandler={() => setPlanConfirming(true)}
                />
              </div>
            </div>
          </Card>
        ) : null}

        <Card
          title={Localized.BillingDetailsTitle}
          actions={
            <div className={Styles.headerActions}>
              {accessBanner?.canRefresh ? (
                <Button
                  text={refreshBusy ? Localized.BillingRefreshing : Localized.BillingRefreshStatus}
                  disabled={refreshBusy}
                  classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
                  classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
                  onClickHandler={() => void refreshStatus()}
                />
              ) : null}
              <Button
                text={portalBusy ? Localized.DashLoading : Localized.BillingManage}
                disabled={portalBusy}
                classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
                classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
                onClickHandler={() => void openPortal()}
              />
            </div>
          }
        >
          {subscription.loading ? (
            <LoadingView className={Styles.panelLoading} />
          ) : subscription.data ? (
            <DataList
              rows={[
                // Prefer the current-plan's localized name (resolved for the dashboard language) over the subscription's stored English plan-name snapshot so this row reflows on a language change.
                subscription.data.planName ? { label: Localized.BillingPlanCurrent, value: currentPlan.data?.name ?? subscription.data.planName } : null,
                { label: Localized.SharedStatus, value: <StatusBadge status={subscription.data.status} /> },
                {
                  label: Localized.BillingPeriod,
                  hint: Localized.BillingPeriodHint,
                  value: `${FormatTimestamp(subscription.data.currentPeriodStart)} – ${FormatTimestamp(subscription.data.currentPeriodEnd)}`
                },
                subscription.data.cancelAtPeriodEnd ? { label: Localized.BillingPendingChange, value: `${Localized.BillingCancelsOn} ${FormatTimestamp(subscription.data.currentPeriodEnd)}` } : null
              ]}
            />
          ) : (
            <Label classNameContainer={Styles.muted} text={Localized.BillingNoSubscription} />
          )}
        </Card>
      </div>

      <div className={Styles.accountStack}>
        <Card title={Localized.BillingUsage}>
          {summary.loading ? (
            <LoadingView className={Styles.panelLoading} />
          ) : summary.data && summary.data.totals.length > 0 ? (
            <TableWrap>
              <table className={`${Styles.table} ${Styles.tableAuto}`}>
                <thead>
                  <tr>
                    <th>
                      <Label text={Localized.BillingMeter} />
                    </th>
                    <th>
                      <Label text={Localized.BillingQuantity} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.data.totals.map((total) => (
                    <tr key={total.meterKey}>
                      <td>
                        <Label text={FormatMeterLabel(total.meterKey)} />
                      </td>
                      <td>
                        <Label text={String(total.quantity)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <EmptyView message={Localized.DashEmpty} />
          )}
        </Card>

        <Card title={Localized.BillingPlanTitle}>
          {currentPlan.loading ? (
            <LoadingView className={Styles.panelLoading} />
          ) : currentPlanPricing ? (
            <DataList
              rows={[
                { label: Localized.BillingPlanMonthly, value: currentPlanPricing.free ? Localized.SharedFree : `${currentPlanPricing.monthly}${Localized.SharedPerMonth}` },
                currentPlanPricing.setupFee ? { label: Localized.BillingPlanSetupFee, value: currentPlanPricing.setupFee } : null,
                currentPlanPricing.features.length > 0
                  ? {
                      label: Localized.BillingPlanIncluded,
                      value: (
                        <ul className={Styles.unitStack}>
                          {currentPlanPricing.features.map((feature) => (
                            <li key={feature}>
                              <Label text={feature} />
                            </li>
                          ))}
                        </ul>
                      )
                    }
                  : null
              ]}
            />
          ) : (
            <Label classNameContainer={Styles.muted} text={Localized.BillingPlanUnavailable} />
          )}
        </Card>
      </div>
    </div>
  );
}
